import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { PACK_SIZES, fmtMoney } from "@/lib/pharmacy";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";

function unitPriceFor(product, pack) {
  const sale = Number(product.sale_price_per_pill || 0);
  if (sale > 0) return sale * pack;
  const raw = Number((product.price_per_pack || {})[String(pack)] || 0);
  const disc = Number(product.discount_percent || 0) / 100;
  return raw * (1 - disc);
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (u) => u ? (u.startsWith("/") ? `${BACKEND}${u}` : u) : "";

function availablePacks(product) {
  const sale = Number(product.sale_price_per_pill || 0);
  if (sale > 0) return PACK_SIZES;
  return PACK_SIZES.filter((s) => (product.price_per_pack || {})[String(s)]);
}

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const packs = availablePacks(product);
  const [pack, setPack] = useState(packs.includes(100) ? 100 : (packs[0] || 100));
  const unit = unitPriceFor(product, pack);
  const perPill = Number(product.sale_price_per_pill || 0);
  const originalPerPill = Number(product.original_price_per_pill || 0);
  const disc = Number(product.discount_percentage || 0);

  return (
    <div className="card-flat overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition group" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block relative aspect-square bg-slate-100">
        <img src={resolveImage(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
        {product.prescription_required && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-blue-50 text-blue-700 text-[10px] font-semibold px-2 py-1 rounded-full border border-blue-200">
            <ShieldAlert className="h-3 w-3"/> Rx
          </div>
        )}
        {disc > 0 && (
          <div className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">-{Math.round(disc)}%</div>
        )}
      </Link>
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{product.category}</div>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display font-semibold text-slate-900 mt-1 truncate">{product.name}</h3>
        </Link>
        <div className="text-xs text-slate-500 truncate">{product.generic_name}{product.strength ? ` · ${product.strength}` : ""}</div>

        {perPill > 0 && (
          <div className="mt-2 text-[11px] font-mono text-slate-500">
            {fmtMoney(perPill)}/pill{originalPerPill > perPill ? <span className="ml-1 line-through">{fmtMoney(originalPerPill)}</span> : null}
          </div>
        )}

        <div className="flex flex-wrap gap-1 mt-3">
          {packs.map((s) => (
            <button
              key={s}
              onClick={() => setPack(s)}
              className={`text-[11px] font-mono px-2 py-1 rounded-full border transition ${pack === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-900"}`}
              data-testid={`pack-${product.id}-${s}`}
            >{s}</button>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3">
          <div>
            <div className="text-xs text-slate-500">Pack of {pack}</div>
            <div className="font-display font-bold text-slate-900 font-mono">{fmtMoney(unit)}</div>
          </div>
          <button
            className="btn-primary text-xs py-2 px-3"
            onClick={() => { addItem(product, pack, 1); toast.success(`${product.name} added`); }}
            data-testid={`add-to-cart-${product.id}`}
          >Add</button>
        </div>
      </div>
    </div>
  );
}
